import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { MessageDescriptor } from '@lingui/core';

import BetterContact from './BetterContact';
import { theme } from '../../../styles/GlobalStyle';
import { Row } from '../../../../components/page-components-copy/components';
import { ROUTE_PATHS } from '../../../../components/_pages/_utils/router.utils';

import { NavNode, TNavNodeChild } from './NavNode';
import { useScrollPosition } from '../../../../hooks/useScrollPosition';
import ButtonStyled from '../../../Button';
import { useAuthContext } from '../../../../providers/auth.provider';
import { useWindowWidth } from '../../../../hooks/useWindowSize';
import { IMAGES, getImage, handleTap } from '../../../../utils';
import getStorage from '../../../../utils/storage.utils';
import { LanguageToggle } from '../../../LanguageToggle';
import { useLocationContext } from '../../../../providers/location.provider';
import { DEBT_GOAL } from '../../../../components/Optimizer/types';

const logo = getImage(IMAGES.SOLVE_LOGO_BLACK_TEXT.ref);

/**
 * Styled Links
 */
export const NavLink = styled(Link)<{ to: string; currentPage?: string }>`
   font-weight: bold;
   font-family: 'Majorant-Regular', Helvetica;
   font-size: 1rem;
   font-weight: 400;
   white-space: nowrap;
   text-decoration: none;
   margin: auto;
   margin-right: 2rem;
   color: ${({ theme }) => theme.gray7} !important;

   &:hover {
    font-weight: bold;
  }
}

   ${({ to, currentPage }) =>
     (currentPage || '').startsWith(to) &&
     css`
       &&& {
         text-decoration: underline;
       }
     `}
 }`;

const Nav = styled('nav')<{ scrolled: boolean }>`
  box-shadow: ${({ scrolled }) => (scrolled ? '0px 4px 4px rgba(0, 0, 0, 0.1)' : 'none')};
`;

/**
 * Links to render
 */
type NestedNavNodeChild = {
  to: string;
  title?: MessageDescriptor;
  subtext?: MessageDescriptor;
};

// A "product" link kicks off onboarding (like Get Started) with a pre-selected
// goal and loan-interest status, rather than navigating to a URL.
type ProductNavChild = {
  title: MessageDescriptor;
  subtext?: MessageDescriptor;
  goal: DEBT_GOAL;
  loanInterest: boolean;
};

type HeaderLink =
  | {
      type: 'logo';
      element: React.ReactNode;
    }
  | {
      type: 'link';
      to: string;
      label: MessageDescriptor;
    }
  | {
      type: 'nested';
      title: MessageDescriptor;
      subtext?: MessageDescriptor;
      nodes: NestedNavNodeChild[];
    }
  | {
      type: 'products';
      title: MessageDescriptor;
      subtext?: MessageDescriptor;
      nodes: ProductNavChild[];
    };

const logoLink: HeaderLink = {
  type: 'logo',
  element: <img src={logo} style={{ width: '113px', marginRight: '32px' }} key={'logo'} />,
};

const toolsLink: HeaderLink = {
  type: 'nested',
  title: msg`Tools`,
  nodes: [
    {
      to: 'https://app.solve.finance',
      title: msg`Credit Help`,
      subtext: msg`Put borrowing on automatic with our full service app`,
    },
    {
      to: 'https://solve.finance/how-to-borrow-online/',
      title: msg`Borrow Online`,
      subtext: msg`Free: how to borrow online quickly`,
    },
    {
      to: 'https://www.solve.finance/how-to-borrow-against-your-house/',
      title: msg`Borrow From Your House`,
      subtext: msg`Free: how do you get equity out of your home?`,
    },
    {
      to: 'https://www.solve.finance/independent-mortgage-advice',
      title: msg`Home Finance`,
      subtext: msg`Free: independent mortgage calculators`,
    },
    {
      to: 'https://solve.finance/how-much-house-can-i-afford-calculator',
      title: msg`Home Affordability Maximizer`,
      subtext: msg`Free: estimate your home-buying power`,
    },
    {
      to: 'https://www.solve.finance/mortgage-dti-calculator',
      title: msg`DTI Calculator`,
      subtext: msg`Free: quickly calculate your debt-to-income ratio`,
    },
    {
      to: 'https://www.solve.finance/how-to-improve-your-credit-score',
      title: msg`Improve Credit Score`,
      subtext: msg`Free: how to make your credit score better`,
    },
  ],
};

const pricingLink: HeaderLink = { type: 'link', to: '/pricing', label: msg`Pricing` };

const aboutLink: HeaderLink = { type: 'link', to: ROUTE_PATHS.ABOUT, label: msg`About` };

const resourcesLink: HeaderLink = { type: 'link', to: 'https://www.solve.finance/resources/', label: msg`Resources` };

// PR-only. Each item starts the onboarding flow (creates the anonymous Firebase user,
// then routes to /onboarding) with a pre-selected goal + loan-interest status.
const productsLink: HeaderLink = {
  type: 'products',
  title: msg`Products`,
  nodes: [
    {
      title: msg`Personal Loan`,
      subtext: msg`Get the cash you need with a personal loan`,
      goal: DEBT_GOAL.GET_CASH,
      loanInterest: true,
    },
    {
      title: msg`Credit Builder`,
      subtext: msg`Build your credit with a credit builder loan`,
      goal: DEBT_GOAL.IMPROVE_CREDIT,
      loanInterest: true,
    },
    {
      title: msg`Free Credit Report`,
      subtext: msg`Check your credit report for free`,
      goal: DEBT_GOAL.IMPROVE_CREDIT,
      loanInterest: false,
    },
  ],
};

/**
 * The header navigation bar.
 */
const Header: React.FC<{
  currentPage: string;
  handleSignOut: () => Promise<void>;
  className?: string;
  disableAccountLinks?: boolean;
}> = ({ handleSignOut, className, disableAccountLinks }) => {
  const storage = getStorage();
  const { handleSignUp, firebaseUser } = useAuthContext();
  const { width } = useWindowWidth();
  const { y: scrollY } = useScrollPosition();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const params = new URLSearchParams(search);
  const [source, setSource] = useState<string | null>(params.get('utm_source') || params.get('utm_content'));

  // Use the shared location provider so the `?location=` override (and IP/address
  // resolution) is respected — not just the device timezone/navigator language.
  const { isPuertoRico: isPR } = useLocationContext();

  // Logo returns a signed-in (non-anonymous) user to their dashboard, everyone
  // else to the home page.
  const logoTarget = firebaseUser && !firebaseUser.isAnonymous ? ROUTE_PATHS.DASHBOARD : ROUTE_PATHS.HOME;

  // PR gets a stripped-down nav: no links out to the www marketing site, and a
  // PR-only "Products" dropdown that drops users straight into onboarding.
  const leftHeaderLinks: HeaderLink[] = isPR
    ? [logoLink, productsLink, aboutLink]
    : [logoLink, toolsLink, pricingLink, aboutLink, resourcesLink];

  const startOnboardingWithGoal = (goal: DEBT_GOAL, loanInterest: boolean) => {
    handleSignUp();

    const query = new URLSearchParams({ goal, loanInterest: String(loanInterest) });
    navigate(`${ROUTE_PATHS.ONBOARDING}?${query.toString()}`);
  };

  const hideAuthRow =
    disableAccountLinks ||
    pathname.includes(ROUTE_PATHS.ONBOARDING) ||
    pathname.includes(ROUTE_PATHS.DASHBOARD) ||
    pathname === ROUTE_PATHS.BETTER ||
    source === 'better';

  const hideResources = width < 810;
  const hideGetStarted = width < 933;

  const isExternal = (url) => url.startsWith('https');

  useEffect(() => {
    const hydrate = async () => {
      const storedSource = await storage.getItem('solve_source');

      if (!storedSource) {
        setSource(storedSource);
      }
    };

    hydrate();
  }, [source]);

  return (
    <Nav className={className} scrolled={scrollY > 0}>
      <div id="header-content" role="menubar">
        <div>
          {leftHeaderLinks.map((item, index) => {
            if (item.type === 'logo') {
              return (
                <Link to={logoTarget} key={`logo-${index}`} aria-label="Solve Finance home">
                  {item.element}
                </Link>
              );
            }

            if (item.type === 'nested') {
              const title = i18n._(item.title);
              const subtext = item.subtext ? i18n._(item.subtext) : undefined;
              const resolvedNodes: TNavNodeChild[] = item.nodes.map((node) => ({
                to: node.to,
                title: node.title ? i18n._(node.title) : undefined,
                subtext: node.subtext ? i18n._(node.subtext) : undefined,
              }));
              return <NavNode title={title} subtext={subtext} nodes={resolvedNodes} key={`${title}-${index}`} />;
            }

            if (item.type === 'products') {
              const title = i18n._(item.title);
              const subtext = item.subtext ? i18n._(item.subtext) : undefined;
              const resolvedNodes: TNavNodeChild[] = item.nodes.map((node) => ({
                title: i18n._(node.title),
                subtext: node.subtext ? i18n._(node.subtext) : undefined,
                onClick: () => startOnboardingWithGoal(node.goal, node.loanInterest),
              }));
              return <NavNode title={title} subtext={subtext} nodes={resolvedNodes} key={`${title}-${index}`} />;
            }

            const label = i18n._(item.label);
            if (hideResources && label === i18n._(msg`Resources`)) {
              return null;
            }

            return (
              <NavLink
                as={isExternal(item.to) ? 'a' : undefined}
                to={isExternal(item.to) ? undefined : item.to}
                href={isExternal(item.to) ? item.to : undefined}
                key={`${item.to}-${index}`}
                role="menuitem"
                target={isExternal(item.to) ? '_blank' : undefined}
                rel={isExternal(item.to) ? 'noreferrer noopener' : undefined}
              >
                {label}
              </NavLink>
            );
          })}
          {(pathname === ROUTE_PATHS.BETTER || (source === 'better' && pathname === '/')) && hideAuthRow && (
            <BetterContact />
          )}
        </div>
        <Row className="auth-row">
          <LanguageToggle className="mr-3 min-w-[110px]" size="sm" />
          {!hideAuthRow && (
            <>
              <Link to={ROUTE_PATHS.LOGIN}>
                <Trans>Log In</Trans>
              </Link>
              {!hideGetStarted && (
                <ButtonStyled
                  style={{
                    borderRadius: 8,
                    fontSize: 14,
                    height: '32px',
                    minHeight: '32px',
                    width: '110px',
                    minWidth: '110px',
                    color: theme.white,
                    backgroundColor: theme.blue4,
                    border: `2px solid ${theme.blue4}`,
                    padding: 0,
                    marginRight: '12px',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => {
                    handleSignUp();

                    navigate(ROUTE_PATHS.ONBOARDING);
                  }}
                >
                  <Trans>Get Started</Trans>
                </ButtonStyled>
              )}
            </>
          )}
        </Row>
      </div>
    </Nav>
  );
};

/**
 * The Final Exported Container
 */
const StyledHeader = styled(Header)`
  position: fixed;
  top: 0;
  background-color: #fff;
  display: flex;
  width: 100%;
  justify-content: center;
  z-index: 1;
  height: 68px;

  &.scrolled {
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
  }

  @media print {
    display: none;
  }

  #header-content {
    display: flex;
    max-width: 1200px;
    height: calc(env(safe-area-inset-top) + 69px);
    justify-content: space-between;
    flex-wrap: wrap;
    width: 100%;
  }
  @media screen and (max-width: 480px) {
    #header-content > div > ul {
      display: none !important;
    }
    #header-content div > button {
      display: none !important;
    }
  }

  @media screen and (max-width: 1200px) {
    #header-content {
      padding: 0 32px;
    }
  }

  #header-content > div:nth-child(1) {
    display: flex;
    justify-content: flex-start;
  }

  #header-content > div:nth-child(2) {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  #header-content > div > ul,
  #header-content > div > a {
    position: relative;
    display: flex;
    margin-right: 24px;
    align-items: center;
    cursor: pointer;
  }
  #header-content > div > ul > svg {
    width: 24px;
  }
  #header-content > div > a > svg {
    margin-right: 32px;
    width: 113px;
  }
  & .auth-row {
    a,
    a:hover,
    a:visited,
    a:active,
    a:focus {
      color: ${({ theme }) => theme.gray7};
    }
  }
`;

export default StyledHeader;
